import React, { useMemo } from 'react';
import { Card, Empty, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { BqRetentionPoint } from '@/types';
import { RetentionRow, dayLabel, fmtNum, fmtPct, fmtUsd, retentionMatrix } from './bqMath';

const DAYS = [1, 3, 7, 14, 30];

const COLUMNS: ColumnsType<RetentionRow> = [
  { title: 'Install day', dataIndex: 'cohortDate', key: 'cohortDate', render: dayLabel },
  {
    title: 'Installs',
    dataIndex: 'cohortSize',
    key: 'cohortSize',
    align: 'right',
    render: (v: number) => fmtNum(v),
  },
  ...DAYS.map((n): ColumnsType<RetentionRow>[number] => ({
    title: `D${n}`,
    key: `d${n}`,
    align: 'right',
    // A day the cohort has not reached yet stays blank; a dash means no value.
    render: (_: unknown, r: RetentionRow) => (n in r.rates ? fmtPct(r.rates[n]) : ''),
  })),
  {
    title: 'Revenue / install',
    key: 'ltv',
    align: 'right',
    render: (_: unknown, r: RetentionRow) =>
      r.ltvUsd === null ? '—' : `${fmtUsd(r.ltvUsd, 4)} (D${r.ltvDay})`,
  },
];

/** Retention and revenue per install by install day. */
export const BqRetentionTable: React.FC<{ retention: BqRetentionPoint[] }> = ({ retention }) => {
  const rows = useMemo(() => retentionMatrix(retention), [retention]);
  return (
    <Card size="small" title="Retention by install day">
      {rows.length === 0 ? (
        <Empty description="No install day in this range" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Table
          size="small"
          rowKey="cohortDate"
          columns={COLUMNS}
          dataSource={rows}
          pagination={rows.length > 14 ? { pageSize: 14, size: 'small' } : false}
          scroll={{ x: true }}
        />
      )}
    </Card>
  );
};
